/**
 * useFeaturedEntityHandlers Hook
 * 
 * Provides handlers for featured entity actions (HomePageMap only).
 * Extracted to eliminate duplication and improve maintainability.
 * 
 * @module hooks/useFeaturedEntityHandlers
 */

import { useCallback } from 'react'
import { toast } from '../services/toastService'
import { entityAPI } from '../services/api'
import { createLogger } from '../utils/logger'

const logger = createLogger('useFeaturedEntityHandlers')

/**
 * Hook for featured entity interaction handlers
 * @param {Object} options - Handler options
 * @param {Function} options.handleSelectEntity - Select entity handler
 * @param {Function} options.setUserLocation - Set user location function
 * @param {Function} options.setSearchLocation - Set search location function
 * @param {Function} options.setSearchLocationName - Set search location name function
 * @param {Function} options.fetchNearbyEntities - Fetch nearby entities function
 * @param {Function} options.setLocationSource - Set location source function
 * @param {Object} options.mapRef - Map reference
 * @param {Function} options.setSelectedEntityId - Set selected entity ID function
 * @param {Function} options.setFocusedEntityId - Set focused entity ID function
 * @param {Function} options.onTabChange - Tab change callback
 * @param {Function} options.onChannelSelected - Channel selected callback
 * @param {Object} options.user - Current user object
 * @param {Function} options.t - Translation function
 * @returns {Object} Featured entity handlers
 */
export const useFeaturedEntityHandlers = ({
  handleSelectEntity,
  setUserLocation,
  setSearchLocation,
  setSearchLocationName,
  fetchNearbyEntities,
  setLocationSource,
  mapRef,
  setSelectedEntityId,
  setFocusedEntityId,
  onTabChange,
  onChannelSelected,
  user,
  t
}) => {
  /**
   * Handle "View Details" button click on featured entity
   */
  const handleFeaturedViewDetails = useCallback((entity) => {
    handleSelectEntity(entity)
  }, [handleSelectEntity])

  /**
   * Handle "Show on Map" button click on featured entity
   */
  const handleFeaturedShowOnMap = useCallback((entity) => {
    if (entity.currentLatitude && entity.currentLongitude) {
      const location = { 
        latitude: entity.currentLatitude, 
        longitude: entity.currentLongitude 
      }
      
      // Update map location
      setUserLocation(location)
      setSearchLocation(entity.currentAddress || '')
      if (entity.currentAddress) {
        setSearchLocationName(entity.currentAddress)
      }
      
      // Fetch nearby entities at featured entity's location
      fetchNearbyEntities(location)
      setLocationSource('geocoded')
      
      // Center map on entity after a brief delay
      setTimeout(() => {
        if (mapRef.current) {
          mapRef.current.setView([location.latitude, location.longitude], 14, {
            animate: true,
            duration: 1
          })
        }
      }, 300)
      
      // Highlight the entity
      setSelectedEntityId(entity.id)
      setFocusedEntityId(entity.id)
    }
  }, [
    setUserLocation, 
    setSearchLocation, 
    setSearchLocationName, 
    fetchNearbyEntities, 
    setLocationSource, 
    mapRef, 
    setSelectedEntityId, 
    setFocusedEntityId
  ])

  /**
   * Handle "Join Channel" button click on featured entity
   */
  const handleFeaturedJoinChannel = useCallback(async (entity) => {
    if (!user) {
      toast.info(t('channel.signUpToJoin'))
      if (onTabChange) {
        onTabChange('auth')
      }
      return
    }

    try {
      const response = await entityAPI.getEntityChannel(entity.id)
      const channelId = response.data.channelId

      // Show success toast immediately
      toast.success(t('channel.discussionJoined'))

      // Navigate to contact tab with channel in both immersive and normal modes
      if (onTabChange) {
        // Immersive mode: switch to contact tab with short delay
        // Backend invalidates cache immediately, so channels list will be fresh
        setTimeout(() => {
          onTabChange('contact', null, channelId)
        }, 500)
      } else if (onChannelSelected) {
        // Normal mode: use channel selected callback
        onChannelSelected(channelId)
      }
    } catch (error) {
      logger.error('Failed to join featured entity channel', error)
      toast.error(t('channel.failedToJoinChannel'))
    }
  }, [user, t, onTabChange, onChannelSelected])

  return {
    handleFeaturedViewDetails,
    handleFeaturedShowOnMap,
    handleFeaturedJoinChannel
  }
}
